export interface SyncJobStatus {
  id:string;
  accountId:string;
  accountName?:string|null;
  dateFrom?:string;
  dateTo?:string;
  phase:string;
  stage?:string;
  error:string|null;
  dispatched?:boolean;
  waitReason?:string|null;
  nextAttemptAt?:number;
  evaluationStatus?:'pending'|'delivered'|'not-required'|'not-published';
  publishReadyAt?:number|null;
  lastDispatchedAt?:number|null;
}

export interface SyncProgress {
  pending:number;
  completed:number;
  failed:number;
  cancelled:number;
  total:number;
  queued:number;
  waitingMeta:number;
  downloading:number;
  insightsDownloading?:number;
  metadataDownloading?:number;
  publishing:number;
  retry?:number;
  waitSummary?:string;
  errorSummary?:string;
  stageSummary?:string;
}

function syncErrorText(job:SyncJobStatus):string {
  const error=job.error==='Meta async report failed'
    ?'Meta не змогла сформувати звіт; причину попередній код не зберіг. Повторіть Sync для цього кабінету та перевірте логи у «Звітах».'
    :job.error;
  const account=job.accountName?`${job.accountName} (${job.accountId})`:job.accountId;
  const period=job.dateFrom?` [${job.dateFrom} — ${job.dateTo}]`:'';
  return `${account}${period}: ${error}${error?.includes('Job ID:')?'':` (Job ID: ${job.id})`}`;
}

/** UI status reads only. Server-side Meta polling and job scheduling are unchanged. */
export function syncStatusPollDelay(progress:SyncProgress):number {
  if(progress.pending>0&&(progress.retry??0)>=progress.pending)return 30000;
  if(progress.publishing>0)return 4000;
  if(progress.pending<=2&&progress.waitingMeta===0)return 4000;
  return 10000;
}

export function summarizeSyncJobs(jobs:SyncJobStatus[]):SyncProgress {
  const waiting=jobs.filter(job=>!['completed','failed','cancelled'].includes(job.phase)&&(job.waitReason?.includes('cooldown')||job.waitReason==='operation retry'));
  const count=(phases:string[])=>jobs.filter(job=>phases.includes(job.phase)).length;
  const readyCount=(phases:string[])=>jobs.filter(job=>phases.includes(job.phase)&&!waiting.includes(job)&&job.dispatched!==false).length;
  const completed=count(['completed']);
  const failed=count(['failed']);
  const cancelled=count(['cancelled']);
  const appWaiting=waiting.filter(job=>job.waitReason==='app cooldown');
  const nextAppAttempt=Math.max(0,...appWaiting.map(job=>job.nextAttemptAt??0));
  const errors=jobs.filter(job=>job.error&&job.waitReason!=='app cooldown');
  return {
    stageSummary:[...new Set(jobs.filter(j=>!['completed','failed','cancelled'].includes(j.phase)).map(j=>j.stage==='wide'?'Wide: inventory / campaign report':j.stage==='detail'?'Sync: ad report / scoped metadata':j.stage==='children'?'Children: fresh metadata':'Sync'))].join('; '),
    total:jobs.length,completed,failed,cancelled,pending:jobs.length-completed-failed-cancelled,
    queued:jobs.filter(job=>!waiting.includes(job)&&!['failed','completed','cancelled'].includes(job.phase)&&(job.phase==='start'||job.dispatched===false)).length,waitingMeta:readyCount(['poll']),
    downloading:readyCount(['insights','campaigns','adsets','ads','account']),
    insightsDownloading:readyCount(['insights']),
    metadataDownloading:readyCount(['campaigns','adsets','ads','account']),
    publishing:count(['publish']),retry:waiting.length,
    waitSummary:appWaiting.length?`Meta тимчасово обмежила застосунок. На паузі: ${appWaiting.length}. ${nextAppAttempt?`Наступна спроба о ${new Date(nextAppAttempt).toLocaleTimeString('uk-UA')}. `:''}Синхронізація продовжиться автоматично.`:waiting.slice(0,3).map(job=>`${job.accountId}: ${job.waitReason}`).join('; ')||undefined,
    errorSummary:errors.length?errors.slice(0,3).map(syncErrorText).join('; ')
      +(errors.length>3?`; ще ${errors.length-3} з помилкою`:''):undefined,
  };
}
